import { useEffect, useState } from 'react';
import { CustomTabItem } from '@shared/types/custom-tabs';

import {
  getBottomMenuOptions,
  getMenuOptions,
  menuType,
  prepareCustomTabs,
} from '@/components/Nav/utils';

import { MenuOption } from '@/types/menu-options';
import useAccountData from '@/hooks/useAccountData';
import useCustomTabsPermissions from '@/hooks/useCustomTabsPermissions';
import MainMenuItem from '@/components/Nav/MainMenuItem';
import 'react-toastify/dist/ReactToastify.css';
import InvestorsMenu from '@/components/Nav/InvestorsMenu';
import { IUserContext, useUserContext } from '@/context/UserContext';

function MainMenu() {
  const [customTabInfo, setCustomTabInfo] = useState<CustomTabItem[] | null>(
    null
  );
  const [menuOptions, setMenuOptions] = useState<MenuOption[]>([]);
  const [bottomMenuOptions, setBottomMenuOptions] = useState<MenuOption[]>([]);
  const {
    impersonatingAccountId,
    auth,
    companyChatbotEnabled,
    companyName,
  }: IUserContext = useUserContext();
  const { getAccountData } = useAccountData();
  const { useCustomTabs } = useCustomTabsPermissions();

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, no-nested-ternary
  const { data } = impersonatingAccountId
    ? getAccountData(impersonatingAccountId)
    : auth?.clients?.[0]?.id
    ? getAccountData(auth?.clients?.[0]?.id)
    : { data: null };

  const clientId = auth?.isClient
    ? auth?.clients?.[0]?.id
    : impersonatingAccountId || undefined;

  const { data: customTabs, isLoading } = useCustomTabs(
    auth?.id as number,
    clientId
  );
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const showTransactionsTab = !!data;

  useEffect(() => {
    if (customTabs) setCustomTabInfo(customTabs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, isLoading]);

  useEffect(() => {
    const menu = getMenuOptions(
      menuType(impersonatingAccountId, auth),
      auth,
      prepareCustomTabs(customTabInfo),
      impersonatingAccountId,
      companyChatbotEnabled,
      showTransactionsTab,
      companyName || ''
    );
    const bottomMenu = getBottomMenuOptions(
      menuType(impersonatingAccountId, auth),
      auth,
      impersonatingAccountId
    );

    // Set the menu options without the action
    setMenuOptions(menu);
    setBottomMenuOptions(bottomMenu);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonatingAccountId, showTransactionsTab, customTabInfo, companyName]);

  return (
    <>
      <div>
        <ul
          className='menu min-w-full overflow-hidden mt-5 p-1'
          id='MainMenu'
          role='menu'
        >
          {menuOptions.map((option) => (
            <li key={option.label} className='mt-2' role='menuitem'>
              <MainMenuItem {...option} />
            </li>
          ))}
        </ul>
        <InvestorsMenu />
      </div>
      {bottomMenuOptions.length > 0 ? (
        <ul
          className='menu min-w-full overflow-hidden mt-5 p-1 justify-center'
          id='MainMenu'
          role='menu'
        >
          {bottomMenuOptions.map((option) => (
            <li key={option.label} className='mt-2' role='menuitem'>
              <MainMenuItem {...option} />
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

export default MainMenu;
